import WithAuth from '@/components/Auth/WithAuth';
import { DataProvider } from '@/components/context/DataContext';
import { StatusProvider } from '@/components/context/StatusContext';
import { UserProvider } from '@auth0/nextjs-auth0';
import axios from 'axios';
import { signOut } from 'firebase/auth';
import { sessionLogout } from 'services/user.service';

import AppToastContainer from '@/components/AppToastContainer';
import F3KeyListener from '@/components/F3EventListener';
import TradeRequest from '@/components/TradeRequest';
import WindowRegistryClient from '@/components/WindowRegistryClient';
import {
    doc,
    onSnapshot
} from 'firebase/firestore';
import flagsmith from 'flagsmith/isomorphic';
import { FlagsmithProvider } from 'flagsmith/react';
import { saveLastViewedView } from 'helper/lastViewedView';
import { trackEvent } from 'helper/mixPanel';
import { ErrorBoundary, notifyBugSnag } from 'lib/bugsnag';
import { complianceRoles } from 'lib/constants';
import getPageTitle from 'lib/getPageTitle';
import LogRocket from 'logrocket';
import Head from 'next/head';
import { hasPath } from 'ramda';
import { useEffect, useState } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import 'regenerator-runtime/runtime';
import {
    getAuth,
    isAuthenticated,
    logout,
    removeAuth
} from 'services/auth.service';
import {
    db,
    auth as firebaseAuth,
    setDocs, updateDocs
} from 'services/firebase.service';
import { socket, socketConnect, socketEmit } from 'services/socket';
import { registerFocusServiceWorker } from 'services/window-manager.service';
import useUserPreferenceStore from 'store/userPreferenceStore';
import AppLoader from '../atoms/AppLoader';
import Header from '../organisms/Header';
import RailSideBar from '../organisms/RailSideBar';
import '../styles/globals.css';
import 'highcharts/css/stocktools/gui.css';
import 'highcharts/css/annotations/popup.css';
import '../styles/highcharts-plota-popup.css';
import '../styles/advanced-date-picker.css';
import useCommonStore from '@/store/commonStore';

function isValidJSONString(str) { try { if (!str) return false; JSON.parse(str); return true; } catch (error) { return false; } }

let called = false;
async function renewToken(refreshToken, id) {
    called = true;
    return axios.post(`${process.env.NEXT_PUBLIC_API_URL}/auth/refresh`, { refreshToken, id });
}

function MyApp({ Component, pageProps, flagsmithState, router }) {
    const [currentUser, setCurrentUser] = useState(null);

    const initServerTime = useCommonStore((state) => state.init);
    const resetServerTime = useCommonStore((state) => state.reset);

    useEffect(() => {
        setCurrentUser(getAuth());
        registerFocusServiceWorker();
        initServerTime();

        return () => {
            resetServerTime();
        };
    }, []);

    useEffect(() => {
        if (!isAuthenticated()) return;
        useUserPreferenceStore.getState().hydrate();
    }, [currentUser?.id]);

    try {
        if (process.env.NEXT_PUBLIC_LOGROCKET_APP_ID) {
            LogRocket.init(process.env.NEXT_PUBLIC_LOGROCKET_APP_ID, {
                network: {
                    requestSanitizer: (request) => {
                        if (request.headers['x-access-token']) request.headers['x-access-token'] = '**redacted**';
                        if (isValidJSONString(request.body) && JSON.parse(request.body).password) {
                            const body = JSON.parse(request.body);
                            body.password = '**redacted**';
                            request.body = JSON.stringify(body);
                        }
                        return request;
                    },
                    responseSanitizer: (response) => {
                        if (isValidJSONString(response.body) && hasPath(['entity', 'token'], JSON.parse(response.body))) {
                            const body = JSON.parse(response.body);
                            body.entity.token = '**redacted**';
                            body.entity.fbToken = '**redacted**';
                            body.entity.refreshToken = '**redacted**';
                            response.body = JSON.stringify(body);
                        }
                        return response;
                    }
                }
            });
        }
    } catch (error) {
        console.log(error);
    }

    const logoutFromSession = async () => {
        sessionLogout();
        const auth = getAuth();
        try {
            const userDocRef = doc(db, 'presence', auth?.id);
            await updateDocs(userDocRef, { online: false });
            await signOut(firebaseAuth);
        } catch (e) { console.log(e); }
        socketEmit('/post/users/sessions/logout', { sid: auth?.sid });
        socket.disconnect();
        logout();
    };

    const onSocketConnected = () => {
        if (['/matchings/[id]', '/matchings', '/dashboard', '/clob',
            '/irs/[id]', '/indexes/[indexId]', '/currencies/[id]', '/bonds/[bondId]', '/matchings/irs'
        ].some((path) => router.pathname === path)) {
            socketEmit('/post/user-group/:join', router.pathname === '/clob' ? { page: 'clob' } : undefined);
        }
        console.log('socket connected');
    };
    const onSocketDisconnected = (e) => {
        console.log('socket disconnected', e);
        if (e !== 'io client disconnect' && e !== 'io server disconnect') {
            const auth = getAuth();
            notifyBugSnag('socket disconnected', {
                reason: e,
                userId: auth?.id,
                email: auth?.email,
                transport: socket.io?.engine?.transport?.name,
                url: process.env.NEXT_PUBLIC_WEB_SOCKET_BASE_URL
            });
        }
    };
    const onConnectError = async (err) => {
        console.log(`connect_error due to ${err.message}`);
        const auth = getAuth();
        notifyBugSnag('socket connect_error', {
            message: err.message,
            description: err.description,
            context: err.context,
            type: err.type,
            transport: socket.io?.engine?.transport?.name,
            attemptedTransports: socket.io?.opts?.transports,
            url: process.env.NEXT_PUBLIC_WEB_SOCKET_BASE_URL,
            userId: auth?.id,
            email: auth?.email,
            hasToken: !!auth?.token
        });
        if (err.message === 'invalid token' || err.message === 'session_expire') {
            logoutFromSession();
        }
        socket.io.opts.transports = socket.io?.engine?.transport?.name === 'websocket' ? ['polling', 'websocket'] : ['websocket'];
    };

    const onLogout = () => {
        socket.disconnect();
        removeAuth();
        window.location.href = '/api/auth/logout';
    };

    useEffect(() => {
        const onAnyEvent = (name, value) => {
            if (value?.error) {
                if (value.error?.error === 'unauthorized') {
                    logoutFromSession();
                }
            }
        };
        const auth = getAuth();
        if (auth?.id) {
            LogRocket.identify(auth.id, { email: auth.email, name: auth.name, companyName: auth.companyName, role: auth.role, sessionId: auth.sessionId });
        }
        socket.on('connect', onSocketConnected);
        socket.on('disconnect', onSocketDisconnected);
        socket.on('connect_error', onConnectError);
        socket.prependAny(onAnyEvent);
        socket.on('/post/users/sessions/logout', onLogout);
        socketConnect();
        if (isAuthenticated()) {
            socketEmit('/post/users/sessions/:sessionId', { sessionId: auth?.sessionId });
        }

        return () => {
            socket.off('connect', onSocketConnected);
            socket.off('disconnect', onSocketDisconnected);
            socket.off('connect_error', onConnectError);
            socket.off('/post/users/sessions/logout', onLogout);
            socket.offAny(onAnyEvent);
        };
    }, []);

    useEffect(() => {
        const currentUser = getAuth();
        if (currentUser?.id) {
            if (complianceRoles.includes(currentUser.role)) return;
            const presenceRef = doc(db, 'presence', currentUser.id);

            setDocs(presenceRef, { online: true });

            const unsubscribe = onSnapshot(presenceRef, (snapshot) => {
                const data = snapshot.data();
                if (data && !data.online) {
                    setTimeout(() => {
                        const checkAuth = getAuth();
                        if (checkAuth) {
                            setDocs(presenceRef, { online: true });
                        }
                    }, 1000);
                }
            });

            const handleBeforeUnload = () => {
                setDocs(presenceRef, { online: false });
                unsubscribe();
            };
            window.addEventListener('beforeunload', handleBeforeUnload);
            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
            };
        }
    }, []);

    useEffect(() => {
        const handleComplete = (url) => {
            let pageName = url.slice(1);
            if (!pageName) {
                pageName = 'index';
            }
            trackEvent('Page View', { 'Page Name': pageName });
            saveLastViewedView(url.split('?')[0]);
            return true;
        };
        const handleError = () => true;

        router.events.on('routeChangeComplete', handleComplete);
        router.events.on('routeChangeError', handleError);

        const originalWindowOpen = window.open;

        window.open = (...args) => {
            const [url, target] = args;
            let pageName = 'New Window';
            if (url) {
                const urlObj = new URL(url, window.location.origin);
                pageName = urlObj.pathname.slice(1) || 'index';
            }
            trackEvent('Page View', {
                'Page Name': pageName,
                Target: target || '_blank'
            });

            return originalWindowOpen.apply(window, args);
        };

        return () => {
            router.events.off('routeChangeComplete', handleComplete);
            router.events.off('routeChangeError', handleError);
            window.open = originalWindowOpen;
        };
    }, [router.events]);

    const isFlow = router.pathname === '/flow';

    useEffect(() => {
        if (!isFlow) return undefined;
        const html = document.documentElement;
        const prevHtml = html.style.overflow;
        const prevBody = document.body.style.overflow;
        html.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        return () => {
            html.style.overflow = prevHtml;
            document.body.style.overflow = prevBody;
        };
    }, [isFlow]);

    return (
        <>
            <ErrorBoundary>
                <Head>
                    <meta
                        name="viewport"
                        content="width=device-width, minimum-scale=1.0, maximum-scale=1.0, user-scalable=0, viewport-fit=cover"
                    />
                    <title>
                        {getPageTitle(router.pathname)}
                    </title>
                </Head>
                <FlagsmithProvider flagsmith={flagsmith} serverState={flagsmithState}>
                    <UserProvider>
                        <AppToastContainer />
                        <StatusProvider>
                            <DataProvider>
                                <WithAuth router={router}>
                                    <F3KeyListener />
                                    {!isFlow && <Header />}
                                    <AppLoader path={router.pathname} />
                                    <div className={isFlow ? 'flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden' : 'flex min-h-screen'}>
                                        {!isFlow && (
                                            <aside className="sticky top-0 left-0 h-screen shrink-0 z-[999] bg-basic-200">
                                                <RailSideBar />
                                            </aside>
                                        )}
                                        <main className={isFlow ? 'flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden' : 'flex-1 min-w-0'}>
                                            <Component {...pageProps} />
                                        </main>
                                    </div>
                                    <TradeRequest />
                                    <WindowRegistryClient />
                                </WithAuth>
                            </DataProvider>
                        </StatusProvider>
                    </UserProvider>
                </FlagsmithProvider>
            </ErrorBoundary>
        </>
    );
}

MyApp.getInitialProps = async () => {
    await flagsmith.init({ environmentID: process.env.NEXT_PUBLIC_FLAGSMITH_KEY });
    return { flagsmithState: flagsmith.getState() };
};

export default MyApp;
